import { useState } from 'react';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { forgotPassword } from '@biasafe.queries/auth-forgot-password/auth-forgot-password';
import { z } from 'zod';
import {
  AuthErrorBanner,
  AuthSuccessBanner,
  FieldError,
  clearErrorsOnChange,
  firstFieldError,
  shouldShowFieldError,
} from '../components/fieldError';
import {
  AuthHeading,
  AuthPrimaryButton,
  AuthShell,
} from '../components/AuthShell';
import { useUserStore } from '@/entities/user/store';
import { extractErrorMessage } from '@/shared/utils/apiErrors';

/**
 * FINALIZED UI — reviewed against the Figma export and signed off.
 *
 * Do not redesign this screen. If a change to how it LOOKS seems obviously
 * right, read the comment next to the thing first: most of what reads as odd
 * here is a decision with its reason written beside it. See
 * features/Auth/README.md before editing, and change the shared component in
 * components/AuthShell.tsx or components/fieldError.tsx rather than this file,
 * so the flow does not drift apart again.
 *
 * This applies to AI agents as much as to people.
 */
/**
 * Reset Password — step 1: email entry.
 * 1:1 visual port of SignIn.tsx's `ForgotPasswordContent` (step='email')
 * from the Figma export. Backend wiring kept from the previous version.
 */
const FONT_SANS = 'var(--font-brand)';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The address we actually sent to, which doubles as "we have sent it".
  const [sentTo, setSentTo] = useState<string | null>(null);

  // Named once and shared, as LoginPage and SignUpPage do it; the chain was
  // written inline back when one validator used it.
  const emailSchema = z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email');

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      setError(null);
      setIsSubmitting(true);
      try {
        // OUR endpoint, not Firebase's client SDK.
        //
        // Both send a reset mail, and they are not interchangeable. The client
        // SDK's `sendPasswordReset` sends GOOGLE's email -- unbranded, in
        // Google's words -- pointing at GOOGLE's hosted new-password page. The
        // user leaves our product mid-flow and finishes on a page that does not
        // look like us.
        //
        // /auth/forgot-password does the thing we actually want, and already
        // did: it mints the reset link with the Admin SDK, REBASES it onto our
        // own /auth/action ("the new-password form is ours, not Google's"), and
        // delivers it through our branded template with the real link TTL in
        // the copy. It also creates a Firebase account for a user who exists in
        // our table but never migrated, so they are not stranded with nothing
        // to reset.
        //
        // The screen called the SDK anyway, so none of that ran. All of the
        // work was in place and the one call that would have used it was the
        // one call not being made.
        await forgotPassword({ email: value.email });
        // Confirm HERE rather than navigating to /login.
        //
        // This used to `navigate({ to: '/login' })` the moment the mail went
        // out, which told the user nothing: the screen they had just used
        // vanished and the sign-in form came back exactly as it had been. There
        // is no way to tell that from the button having done nothing, and the
        // reasonable response to a button that did nothing is to press it
        // again. The next step is also not on /login -- it is in their inbox --
        // so sending them to the form was pointing at the wrong place too.
        setSentTo(value.email);
      } catch (err) {
        setError(
          extractErrorMessage(err, 'Something went wrong. Please try again.'),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Same landing page as LoginPage — see the note there.
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <AuthShell dataName="ForgotPassword">
      {/* The shared heading, not a local copy of it. The copy here matched on
          font-size and differed on the two things that actually shape a
          headline: tracking was -3.36px against the flow's -1.5px, and the line
          box 63px against 48px. Same 42px, visibly tighter and taller — which
          is why this screen read as a different product while measuring the
          same. */}
      {/* "code" was wrong: Identity Platform mails a LINK and hosts the page
          that takes the new password. Someone waiting for a code to type in
          would sit on this screen watching for something that is never
          coming. */}
      <AuthHeading
        title={sentTo ? 'Check your email' : 'Reset Password'}
      >
        {sentTo
          ? 'Open the link we sent to set a new password.'
          : "We'll email you a link to set a new password."}
      </AuthHeading>

            {sentTo ? (
              <div className="w-full flex flex-col gap-[24px]">
                <AuthSuccessBanner className="w-full">
                  Reset link sent to {sentTo}. It may take a minute to arrive.
                </AuthSuccessBanner>

                {/* Deliberately NOT a resend button. Identity Platform rate-
                    limits these, and a second link invalidates the first — so
                    the impatient path is also the one that breaks the link
                    already sitting in the inbox. Going back re-sends by
                    submitting again, which at least makes it a decision. */}
                <AuthPrimaryButton onClick={() => navigate({ to: '/login' })}>
                  Back to sign in
                </AuthPrimaryButton>

                <button
                  type="button"
                  onClick={() => setSentTo(null)}
                  className="text-[14px] text-[#3e3e3e] hover:underline"
                  style={{ fontFamily: FONT_SANS }}
                >
                  Use a different address
                </button>
              </div>
            ) : (
              <>
              {error && (
                <AuthErrorBanner className="w-full">
                  {error}
                </AuthErrorBanner>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
                className="w-full flex flex-col gap-[24px]"
              >
                <form.Field
                  name="email"
                  validators={{
                    onBlur: emailSchema,
                    // This field had no onSubmit validator at all, so pressing
                    // "Send reset link" on an empty form skipped validation and
                    // asked the API to mail nobody.
                    onSubmit: emailSchema,
                  }}
                  listeners={{ onChange: clearErrorsOnChange }}
                >
                  {(field) => {
                    const hasError = shouldShowFieldError(
                      field.state.meta,
                      form.state.submissionAttempts,
                    );
                    const errorText = firstFieldError(field.state.meta);
                    return (
                      <div className="content-stretch flex flex-col gap-[8px] items-start justify-center relative shrink-0 w-full">
                        <div
                          className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[#121212] text-[16px] text-center text-nowrap"
                          style={{ fontFamily: FONT_SANS }}
                        >
                          <p className="leading-none text-sm font-medium text-[#000000]">
                            Email
                          </p>
                        </div>
                        <div
                          className={`relative rounded-md shrink-0 w-full border shadow-xs ${
                            hasError
                              ? 'border-[#ff4d4f] bg-transparent'
                              : 'border-[#121212]/20 bg-transparent focus-within:border-[#121212]'
                          }`}
                        >
                          <div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
                            <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[12px] py-[0px] relative w-full">
                              <input
                                id="forgot-email"
                                aria-invalid={hasError}
                                aria-describedby={
                                  hasError && errorText
                                    ? `${field.name}-error`
                                    : undefined
                                }
                                type="email"
                                placeholder="Enter your email"
                                name={field.name}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                required
                                autoComplete="email"
                                style={{ fontFamily: FONT_SANS }}
                                className="basis-0 flex flex-col grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#121212] h-9 text-base md:text-sm placeholder:text-muted-foreground bg-transparent border-none outline-none w-full"
                              />
                            </div>
                          </div>
                        </div>
                        {hasError && errorText && (
                          <FieldError id={`${field.name}-error`}>
                            {errorText}
                          </FieldError>
                        )}
                      </div>
                    );
                  }}
                </form.Field>

                <div className="flex gap-4 w-full">
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/login' })}
                    className="flex-1 bg-[#f5f7fa] relative rounded-lg hover:bg-[#e8ebf0] transition-colors cursor-pointer"
                  >
                    <div className="flex flex-row items-center justify-center size-full">
                      <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[16px] py-[8px] relative w-full">
                        <div
                          className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[16px] text-nowrap text-[#121212] font-medium"
                          style={{ fontFamily: FONT_SANS }}
                        >
                          <p className="leading-[24px] whitespace-pre">Cancel</p>
                        </div>
                      </div>
                    </div>
                  </button>
                  {/* The shared primary. Its own copy already matched on colour
                      and radius — this is about there being one of it, so the
                      next change to the button reaches this screen too. */}
                  <div className="flex-1">
                    <AuthPrimaryButton type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send reset link'}
                    </AuthPrimaryButton>
                  </div>
                </div>
              </form>
              </>
            )}
    </AuthShell>
  );
}
